import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, detailMessage } from '../api';
import { useConfirm } from '../components/ConfirmDialog';
import InfoTip from '../components/InfoTip';
import SkillsTable from '../components/SkillsTable';
import { BROWSER_ENGINES, RESOURCE_DEFAULTS, QUOTA_DEFAULTS } from '../components/UserForm';
import { useCrumbLabel } from '../components/shell/crumbs';
import { Badge, CardSkeleton, Icon, PageHeader, StatusBadge, Switch, Tabs } from '../components/ui';
import usePhoneCalls from '../utils/usePhoneCalls';

// Tier label format lifted from the old Users.jsx TierModal — "Name — $X.XX/month cap"
// (or "unlimited" when the cap is null). Same shape the legacy modal showed so the
// admin's mental model carries over.
const formatTierLabel = (tier) => {
  const cap = tier.monthly_cost_limit
    ? `$${parseFloat(tier.monthly_cost_limit).toFixed(2)}/month cap`
    : 'unlimited';
  return `${tier.name}, ${cap}`;
};

// The page splits into tabs that own disjoint slices of the same draft, each with its own
// save. The `channels` tab only ever held the pool-bot assignment, so it is labelled for
// that: channel PAIRING lives on the Account tab. Tools has its own tab and saves without a
// redeploy (Mark, 2026-11-02): grants reach the agent from its next turn, and the rest at
// its next start, which is when a redeploy would have applied them anyway.
const TABS = [
  { value: 'deployment', label: 'Deployment', icon: 'server' },
  { value: 'tools', label: 'Tools', icon: 'tools' },
  { value: 'account', label: 'Account', icon: 'user' },
  { value: 'status', label: 'Status', icon: 'activity' },
  { value: 'channels', label: 'Telegram bot', icon: 'bot' },
];

const TIMEZONES = [
  'UTC', 'Europe/Rome', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'America/New_York', 'America/Los_Angeles', 'America/Chicago',
  'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney',
];

const EXEC_MODES = [
  { value: 'full', label: 'Full (no restrictions)' },
  { value: 'denylist', label: 'Denylist (block dangerous commands)' },
  { value: 'allowlist', label: 'Allowlist (only safe commands)' },
];

// One channel: its switch, and the address it needs revealed underneath while it is on.
function ChannelRow({ id, title, description, checked, onChange, children }) {
  return (
    <div className="channel-item">
      <div className="setting-row p-0">
        <div className="min-w-0">
          <label className="setting-row-title" htmlFor={id}>{title}</label>
          <p className="setting-row-desc">{description}</p>
        </div>
        <Switch id={id} checked={checked} onChange={onChange} />
      </div>
      {children}
    </div>
  );
}

// Save bar for a form built from several cards. `.form-actions.sticky` is made to sit
// INSIDE one card (its negative margins reach that card's edges); as the last child of the
// form it stays in view down the whole tab, so it takes a card's full outline instead.
function SaveBar({ children }) {
  return (
    <div className="card form-actions sticky m-0" style={{ borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-md)' }}>
      {children}
    </div>
  );
}

function SectionHead({ id, title, subtitle, children }) {
  return (
    <div className="card-header">
      <div>
        <h2 className="card-title" id={id}>{title}</h2>
        {subtitle && <p className="card-subtitle">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function EditUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [confirm, confirmDialog] = useConfirm();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([]);
  // Channels tab: list of Telegram bots in the pool, fetched once from
  // /api/telegram-bots. Each entry has id, username, token, user_count.
  // The dropdown shows `@username (N users)` so the admin can favour
  // lightly-loaded bots when reassigning.
  const [bots, setBots] = useState([]);
  const [enabledSkills, setEnabledSkills] = useState([]);
  const [enabledApps, setEnabledApps] = useState([]);
  // The Tools tab's User managed column: tool id -> whether apps may see and change it for
  // this user. Null when the backend could not read it, which hides the column: sending an
  // unread map back would mark every tool admin managed. Only the ids that differ from what
  // was loaded are saved.
  const [userManaged, setUserManaged] = useState(null);
  const [savedUserManaged, setSavedUserManaged] = useState(null);
  const [mandatoryTools, setMandatoryTools] = useState([]);
  // Whether this user is paired on Telegram. Seeded from the loaded user, not defaulted on:
  // a user onboarded from another channel has no Telegram address, and showing the channel
  // as enabled with an empty required id made the Account tab unsavable for them.
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  // Same shape for RossHub. Switching a channel off is how an operator unpairs it: the
  // address is submitted empty, which is what the update endpoint reads as "unpair".
  const [websiteEnabled, setWebsiteEnabled] = useState(false);
  // Same shape again for Microsoft Teams.
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  // And for the phone line: the number the user calls and the one allowed to call it. Only
  // where the environment takes calls (voice_channel_enabled): elsewhere there is no Phone
  // row, and a save sends neither number, so a stored line is left as it is.
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const phoneOn = usePhoneCalls();
  const [useCustomBot, setUseCustomBot] = useState(false);
  const [editingBotToken, setEditingBotToken] = useState(false);
  // MCP server keys (the RossGate sk-gw-… key today): one entry per stored key, masked,
  // from GET /api/users/{id}/mcp-credentials. Empty when the user has none, which hides
  // the card (a server the user never had shows nothing). `credBusy` is the server whose
  // action is in flight.
  const [credentials, setCredentials] = useState([]);
  const [credBusy, setCredBusy] = useState(null);
  // The page splits into tabs ("Deployment" / "Account" / …) that own
  // disjoint slices of the same draft. Tab switches don't lose values —
  // only the visible slice changes.
  const [activeTab, setActiveTab] = useState('deployment');
  // Inline messages for the checks handleSaveAccount makes, keyed by field. Each is
  // cleared as soon as its field is edited or its channel switched.
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    telegram_id: '',
    website_id: '',
    teams_id: '',
    voice_number: '',
    voice_caller: '',
    telegram_bot_token: '',
    // Channels tab: telegram_bot_id is the FK into telegram_bots.id (UUID
    // string). Empty string means "no pool bot assigned": the user is then
    // served by their custom token or the shared default bot
    // (proxy/shared/telegram_bot.py), and nothing assigns a pool bot again.
    // Seeded in fetchData below.
    telegram_bot_id: '',
    newBotToken: '',
    ...RESOURCE_DEFAULTS,
    cpu_limit: '',      // Override for UI (empty string shows placeholder)
    memory_limit_gb: '', // Override for UI
    ...QUOTA_DEFAULTS,
    clear_sessions: false,
    // Status tab: source-agnostic active flag + expiration date. Both
    // are seeded in the fetchData effect from the API response so the
    // tab opens with the user's current state pre-filled. `expires_at`
    // is bound to a datetime-local input and serialized back as ISO
    // when present, or null when the admin clears the field.
    is_active: true,
    expires_at: '',
  });

  // The breadcrumb derives "User 42" from the URL; only this page knows the name.
  useCrumbLabel(user?.username);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userResp, tiersResp, botsResp, credResp] = await Promise.all([
          apiFetch(`/api/users/${userId}`),
          apiFetch('/api/tiers'),
          apiFetch('/api/telegram-bots'),
          apiFetch(`/api/users/${userId}/mcp-credentials`),
        ]);

        if (tiersResp.ok) setTiers(await tiersResp.json());
        if (botsResp.ok) setBots(await botsResp.json());
        if (credResp.ok) setCredentials((await credResp.json()).credentials || []);

        if (userResp.ok) {
          const data = await userResp.json();
          setUser(data);
          setEnabledSkills(data.enabled_tools || []);
          setEnabledApps(data.enabled_apps || []);
          setUserManaged(data.user_managed_tools || null);
          setSavedUserManaged(data.user_managed_tools || null);
          setMandatoryTools(data.mandatory_tools || []);
          // `channels` is the live channel_identities list; telegram_id is the legacy
          // mirror. Either one being present means the channel is paired.
          setTelegramEnabled(
            (data.channels || []).includes('telegram') || !!data.telegram_id
          );
          setWebsiteEnabled(
            (data.channels || []).includes('website') || !!data.website_id
          );
          setTeamsEnabled(
            (data.channels || []).includes('teams') || !!data.teams_id
          );
          setVoiceEnabled(
            (data.channels || []).includes('voice') || !!data.voice_number
          );
          setUseCustomBot(!!data.telegram_bot_token);

          setFormData({
            email: data.email || '',
            telegram_id: data.telegram_id || '',
            website_id: data.website_id || '',
            teams_id: data.teams_id || '',
            voice_number: data.voice_number || '',
            voice_caller: data.voice_caller || '',
            telegram_bot_token: data.telegram_bot_token || '',
            telegram_bot_id: data.telegram_bot_id || '',
            newBotToken: '',
            cpu_reserved: data.cpu_reserved ?? RESOURCE_DEFAULTS.cpu_reserved,
            memory_reserved_gb: data.memory_reserved_gb ?? RESOURCE_DEFAULTS.memory_reserved_gb,
            cpu_limit: data.cpu_limit ?? '',
            memory_limit_gb: data.memory_limit_gb ?? '',
            memory_swap_gb: data.memory_swap_gb ?? RESOURCE_DEFAULTS.memory_swap_gb,
            tier_id: data.tier_id || '',
            cron_min_interval: data.cron_min_interval || QUOTA_DEFAULTS.cron_min_interval,
            cron_max_jobs: data.cron_max_jobs || QUOTA_DEFAULTS.cron_max_jobs,
            max_concurrent_subagents: data.max_concurrent_subagents || QUOTA_DEFAULTS.max_concurrent_subagents,
            exec_mode: data.exec_mode || QUOTA_DEFAULTS.exec_mode,
            browser_engine: data.browser_engine || QUOTA_DEFAULTS.browser_engine,
            timezone: data.timezone || QUOTA_DEFAULTS.timezone,
            clear_sessions: false,
            is_active: data.is_active !== false,
            // datetime-local needs `YYYY-MM-DDTHH:mm` (no seconds, no
            // timezone). The API returns full ISO; slice to 16 chars and
            // strip the trailing 'Z' or offset. Empty string when null,
            // so the input shows blank.
            expires_at: data.coupon_expires_at
              ? new Date(data.coupon_expires_at).toISOString().slice(0, 16)
              : '',
          });
        } else {
          toast.error('User not found');
          navigate('/users');
        }
      } catch (e) {
        toast.error('Failed to load user');
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, navigate]);

  const clearErrors = (...keys) => setErrors(prev => {
    if (!keys.some(k => prev[k])) return prev;
    const next = { ...prev };
    keys.forEach(k => { delete next[k]; });
    return next;
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let finalValue = value;
    if (type === 'number') {
      finalValue = value === '' ? null : parseFloat(value);
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    clearErrors(name);
  };

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  // The internal and platform cards share one list, as the single picker did: the backend
  // splits it into the capability set and the grants (services/users.py).
  const toggleSkill = (id, on) => setEnabledSkills(prev => (on ? [...prev, id] : prev.filter(s => s !== id)));

  // Match the existing button gate at line 362 of the pre-refactor file:
  // any "live" container state blocks redeploy because re-provisioning
  // would yank a working session out from under the user. stopped /
  // pending / not_created are all fine — they just bring the container
  // up with the new config.
  const canRedeploy = (
    user &&
    user.container_status !== 'running' &&
    user.container_status !== 'starting' &&
    user.container_status !== 'stopping'
  );

  // Deployment tab: fields that flow into config.toml or the ECS task
  // definition. Saving here triggers re-provisioning so the container
  // comes back up with the new values. The tools are not among them any more:
  // they are the Tools tab's, saved without a redeploy.
  const handleSaveDeploy = async (e) => {
    e.preventDefault();
    if (!canRedeploy) return;
    // Clearing the history deletes every saved session and cannot be undone, so it is
    // asked once more rather than riding along on a save made for another reason.
    if (formData.clear_sessions && !await confirm({
      title: `Clear ${user.username}'s chat history?`,
      body: <p>Every saved conversation session is deleted when the agent redeploys. This cannot be undone.</p>,
      confirmLabel: 'Clear history and redeploy',
    })) return;
    setSaving(true);
    try {
      // An emptied resource field clears the per-user override. Sending null instead
      // means "leave unchanged" to this sparse update API, so CLEAR_RESOURCE carries the
      // explicit request. A cleared CPU limit uses the protected platform ceiling; it is
      // not unbounded.
      const CLEAR_RESOURCE = -1;
      const resource = (v) => (v === '' || v === null || v === undefined ? CLEAR_RESOURCE : v);
      const body = {
        cpu_reserved: resource(formData.cpu_reserved),
        memory_reserved_gb: resource(formData.memory_reserved_gb),
        cpu_limit: resource(formData.cpu_limit),
        memory_limit_gb: resource(formData.memory_limit_gb),
        memory_swap_gb: resource(formData.memory_swap_gb),
        cron_min_interval: parseInt(formData.cron_min_interval) || 60,
        cron_max_jobs: parseInt(formData.cron_max_jobs) || 3,
        max_concurrent_subagents: parseInt(formData.max_concurrent_subagents) || 3,
        exec_mode: formData.exec_mode || 'full',
        browser_engine: formData.browser_engine || 'lightpanda',
        timezone: formData.timezone || 'UTC',
        clear_sessions: formData.clear_sessions,
        redeploy: true,
      };

      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('User updated & redeployed');
        navigate('/users');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(detailMessage(err.detail, 'Failed to update'));
      }
    } catch (e) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // Tools tab: no redeploy, so it saves while the agent runs, like the account. The
  // grants behind the platform tools reach the agent from its next turn (proxy-tools is
  // told at once); internal tools and apps are written into its configuration and
  // workspace at its next start.
  const handleSaveTools = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        enabled_tools: enabledSkills,
        enabled_apps: enabledApps,
        redeploy: false,
      };
      const managedChanges = Object.fromEntries(
        Object.entries(userManaged || {})
          .filter(([id, on]) => (savedUserManaged || {})[id] !== on),
      );
      if (Object.keys(managedChanges).length > 0) body.user_managed_tools = managedChanges;

      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('Tools saved');
        navigate('/users');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(detailMessage(err.detail, 'Failed to save the tools'));
      }
    } catch (e) {
      toast.error('Failed to save the tools');
    } finally {
      setSaving(false);
    }
  };

  // Marks the field that stopped the save and moves focus to it. The toast each caller
  // raises is kept: it is what the page has always said.
  const flag = (field, message, focusId) => {
    setErrors({ [field]: message });
    document.getElementById(focusId)?.focus();
  };

  // Account tab: DB-only edits. No redeploy, no container side effects.
  // Tier change takes effect on the next request (the proxy-llm path
  // re-reads tier on every request — see services/users.py:480–483).
  const handleSaveAccount = async (e) => {
    e.preventDefault();
    if (telegramEnabled && !formData.telegram_id) {
      flag('telegram_id', 'Enter the Telegram user ID, or switch Telegram off to unpair it.', 'eu-telegram-id');
      toast.error('Telegram User ID is required while the Telegram channel is on');
      return;
    }
    if (websiteEnabled && !(formData.website_id || '').trim()) {
      flag('website_id', 'Enter the member ID, or switch RossHub off to unpair it.', 'eu-website-id');
      toast.error('RossHub Member ID is required while the RossHub channel is on');
      return;
    }
    if (teamsEnabled && !(formData.teams_id || '').trim()) {
      flag('teams_id', 'Enter the Object ID, or switch Microsoft Teams off to unpair it.', 'eu-teams-id');
      toast.error('Entra Object ID is required while the Microsoft Teams channel is on');
      return;
    }
    // The mistake this field invites. Caught before the round trip for the same reason the
    // create form does: "an email cannot be paired" arriving from the server reads as the
    // form being broken rather than as the address being the wrong kind.
    if (teamsEnabled && (formData.teams_id || '').includes('@')) {
      flag('teams_id', 'That is an email address. A Teams message carries the sender\'s Object ID, not their email.', 'eu-teams-id');
      toast.error('A Teams message does not carry the sender\'s email. Use their Entra Object ID.');
      return;
    }
    // A line needs both numbers: with no caller rule nobody can ring it.
    const phoneLine = phoneOn && voiceEnabled;
    if (phoneLine && !(formData.voice_number || '').trim()) {
      flag('voice_number', 'Enter the number to call, or switch the phone off to remove the line.', 'eu-voice-number');
      toast.error('The number to call is required while the phone is on');
      return;
    }
    if (phoneLine && !(formData.voice_caller || '').trim()) {
      flag('voice_caller', 'Enter the number allowed to call: with none, every call is refused.', 'eu-voice-caller');
      toast.error('The allowed caller is required while the phone is on');
      return;
    }
    setErrors({});
    // Switching a channel off sends an empty address, which is what unpairs it. Unpairing
    // EVERY channel is allowed: it leaves the account with nothing to route to, which is a
    // legitimate thing for an operator to want (parking an account, or clearing a wrong
    // pairing before entering the right one). The backend requires an address at CREATION
    // only, and `is_paired` follows from the identities either way.
    const telegramAddress = telegramEnabled ? formData.telegram_id : '';
    const websiteAddress = websiteEnabled ? (formData.website_id || '') : '';
    const teamsAddress = teamsEnabled ? (formData.teams_id || '') : '';
    setSaving(true);
    try {
      const body = {
        // Empty string, not null: null reads as "unchanged", so an address could never be
        // removed. Empty is how the update service clears it, matching the channel fields.
        email: formData.email || '',
        telegram_id: telegramAddress,
        // Sent even when empty, because empty is meaningful here: it UNPAIRS the channel.
        // That is the point of having the field on an edit form, and it is why the create
        // form omits an empty value while this one does not.
        website_id: websiteAddress,
        teams_id: teamsAddress,
        // The phone line: off sends both empty, which removes the line and its rule. Where
        // the environment takes no calls neither is sent (undefined), so nothing changes.
        voice_number: phoneOn ? (phoneLine ? (formData.voice_number || '').trim() : '') : undefined,
        voice_caller: phoneOn ? (phoneLine ? (formData.voice_caller || '').trim() : '') : undefined,
        telegram_bot_token: !useCustomBot
          ? ''
          : (editingBotToken && formData.newBotToken ? formData.newBotToken : undefined),
        tier_id: formData.tier_id ? parseInt(formData.tier_id) : 0,
        redeploy: false,
      };
      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('User updated');
        navigate('/users');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(detailMessage(err.detail, 'Failed to update'));
      }
    } catch (e) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  // The MCP server keys' actions. Superadmin-only on the backend: another admin gets a
  // 403, shown as a toast. Retry only queues the key (the control-plane mints it within
  // seconds); Rotate and Revoke answer once the server has.
  const CRED_ACTIONS = {
    retry: { done: 'Queued: the key is minted within a minute', fail: 'Could not queue the key' },
    rotate: { done: 'Key rotated', fail: 'Could not rotate the key' },
    revoke: { done: 'Key revoked', fail: 'Could not revoke the key' },
  };
  const refreshCredentials = async () => {
    const resp = await apiFetch(`/api/users/${user.id}/mcp-credentials`);
    if (resp.ok) setCredentials((await resp.json()).credentials || []);
  };
  const handleCredentialAction = async (server, action) => {
    if (action === 'revoke' && !(await confirm({
      title: `Revoke the ${server} key?`,
      body: <p>The agent loses access to every service behind it until the key is minted
        again with Retry now. The user's connected accounts are kept.</p>,
      confirmLabel: 'Revoke key',
    }))) return;
    setCredBusy(server);
    try {
      const resp = await apiFetch(
        `/api/users/${user.id}/mcp-credentials/${encodeURIComponent(server)}/${action}`,
        { method: 'POST' },
      );
      const body = await resp.json().catch(() => ({}));
      if (resp.ok) toast.success(CRED_ACTIONS[action].done);
      else toast.error(detailMessage(body.detail, CRED_ACTIONS[action].fail));
      await refreshCredentials();
    } catch (e) {
      toast.error(CRED_ACTIONS[action].fail);
    } finally {
      setCredBusy(null);
    }
  };

  // Status tab: source-agnostic active flag + expiration. Same PUT
  // endpoint as the other tabs but only touches is_active +
  // coupon_expires_at (backend uses `clear_expiration: true` to wipe
  // the date — distinct from "leave unchanged" which omits the field).
  // No redeploy: the active flag is enforced at the proxy-channels
  // gate, not at the container level. If the admin also wants to spin
  // a previously-stopped container back up, they use the Deployment
  // tab's Save & Redeploy.
  const handleSaveStatus = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        is_active: formData.is_active,
        redeploy: false,
      };
      if (formData.expires_at) {
        // datetime-local emits local-time string with no zone (e.g.
        // "2026-06-01T09:00"). Construct a Date from it so the browser
        // resolves it as local, then send ISO (which carries the UTC
        // offset) so the backend stores an unambiguous instant.
        body.coupon_expires_at = new Date(formData.expires_at).toISOString();
      } else {
        body.clear_expiration = true;
      }

      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('Status updated');
        navigate('/users');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(detailMessage(err.detail, 'Failed to update status'));
      }
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // Channels tab: pool-bot reassignment. Saving here flips
  // users.telegram_bot_id via the same PUT endpoint as the other tabs.
  // No redeploy: proxy-channels re-reads a user's bot when their row
  // changes and the bridge refreshes its pollers about every 15s, so a
  // mid-conversation reassignment is picked up within ~15s. The user's
  // in-flight session and container are untouched.
  const handleSaveChannels = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telegram_bot_id: formData.telegram_bot_id || '',
          redeploy: false,
        }),
      });
      if (response.ok) {
        toast.success('Channels updated');
        navigate('/users');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(detailMessage(err.detail, 'Failed to update channels'));
      }
    } catch (e) {
      toast.error('Failed to update channels');
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <PageHeader
      eyebrow={<Link to="/users" className="row gap-1"><Icon name="arrowLeft" size={14} />Users</Link>}
      description="Deployment, tools, account, status and bot routing for this user. Each tab saves on its own."
      actions={user ? <StatusBadge status={user.container_status} /> : null}
    >
      <h1 className="page-title">{user ? `Edit ${user.username}` : 'Edit user'}</h1>
    </PageHeader>
  );

  const tabStrip = (
    <div className="page-tabs">
      <Tabs tabs={TABS} value={activeTab} onChange={setActiveTab} variant="line" label="Edit user sections" />
    </div>
  );

  if (loading) {
    return (
      <div>
        {header}
        {tabStrip}
        <CardSkeleton lines={5} />
        <CardSkeleton lines={3} />
      </div>
    );
  }
  if (!user) return <div>{header}</div>;

  // What is SAVED, not what is selected: the select already shows the selection, so the
  // line beneath it says what a save would change from.
  const savedTierName = user.tier_name
    || (user.tier_id ? (tiers || []).find(t => t.id === user.tier_id)?.name : null);
  const tierChanged = String(user.tier_id || '') !== String(formData.tier_id || '');
  const savedBotName = user.telegram_bot_username
    || (bots || []).find(b => b.id === user.telegram_bot_id)?.username;
  const botChanged = String(user.telegram_bot_id || '') !== String(formData.telegram_bot_id || '');
  const timezones = !formData.timezone || TIMEZONES.includes(formData.timezone)
    ? TIMEZONES
    : [formData.timezone, ...TIMEZONES];
  const drainingNote = user.container_status === 'stopping'
    ? 'The agent is stopping…'
    : 'Stop the agent to enable redeploy';
  const fieldError = (key) => errors[key] && (
    <div className="form-error" role="alert">{errors[key]}</div>
  );
  const cancelButton = (
    <button type="button" className="btn btn-secondary" onClick={() => navigate('/users')} disabled={saving}>Cancel</button>
  );
  const saveLabel = (text) => (saving ? <><span className="spinner-sm" /> Saving…</> : text);

  return (
    <div>
      {header}
      {tabStrip}

      {activeTab === 'deployment' && (
        <form onSubmit={handleSaveDeploy} aria-label="Deployment">
          {!canRedeploy && (
            <div className="notice notice-warn" role="status">
              <Icon name="alert" />
              <div className="notice-body">
                <div className="notice-title">{drainingNote}</div>
                Saving this tab re-provisions the agent, which would cut off the session in
                progress, so it is only possible while the agent is not running.
              </div>
            </div>
          )}

          <section className="card" aria-labelledby="eu-resources">
            <SectionHead
              id="eu-resources"
              title="Resources"
              subtitle="Reservations are scheduler requests; limits are hard caps. Empty fields use protected platform defaults."
            />
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-cpu-reserved">CPU reserved (vCPU)</label>
                <input name="cpu_reserved" id="eu-cpu-reserved" type="number" step="0.1" min="0" className="form-input" value={formData.cpu_reserved ?? ''} onChange={handleChange} placeholder="Platform default" />
                <div className="form-hint">Soft reservation for scheduling. Empty restores the platform default.</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="eu-memory-reserved">Memory reserved (GB)</label>
                <input name="memory_reserved_gb" id="eu-memory-reserved" type="number" step="0.1" min="0" className="form-input" value={formData.memory_reserved_gb ?? ''} onChange={handleChange} placeholder="Platform default" />
                <div className="form-hint">Soft reservation for scheduling. Empty restores the platform default.</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-cpu-limit">CPU limit (vCPU)</label>
                <input name="cpu_limit" id="eu-cpu-limit" type="number" step="0.1" min="0.5" className="form-input" value={formData.cpu_limit ?? ''} onChange={handleChange} placeholder="0.5 default" />
                <div className="form-hint">Hard cap. Empty restores the protected ceiling, at least 0.5 vCPU and never below the reservation.</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="eu-memory-limit">Memory limit (GB)</label>
                <input name="memory_limit_gb" id="eu-memory-limit" type="number" step="0.1" min="0.1" className="form-input" value={formData.memory_limit_gb ?? ''} onChange={handleChange} placeholder="Platform default" />
                <div className="form-hint">Hard cap, OOM-killed if exceeded. Empty restores the platform default.</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-memory-swap">Memory swap (GB)</label>
                <input name="memory_swap_gb" id="eu-memory-swap" type="number" step="0.5" className="form-input" value={formData.memory_swap_gb ?? ''} onChange={handleChange} placeholder="0" />
                <div className="form-hint">Disk swap space for overflow memory.</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-browser-engine">Browser engine</label>
                <select name="browser_engine" id="eu-browser-engine" className="form-input" value={formData.browser_engine || 'lightpanda'} onChange={handleChange}>
                  {BROWSER_ENGINES.map(engine => <option key={engine.value} value={engine.value}>{engine.label}</option>)}
                </select>
                <div className="form-hint">Chromium handles modern SPAs and native screenshots. For sustained browser work, explicitly raise both CPU reserved and CPU limit, typically to 2 vCPU.</div>
              </div>
            </div>
          </section>

          <section className="card" aria-labelledby="eu-quotas">
            <SectionHead id="eu-quotas" title="Quotas" subtitle="Scheduling and concurrency limits, and which commands the agent may run." />
            <div className="form-row cols-3">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-cron-interval">Minimum cron interval (min)</label>
                <input name="cron_min_interval" id="eu-cron-interval" type="number" className="form-input" value={formData.cron_min_interval ?? ''} onChange={handleChange} min="60" max="1440" />
                <div className="form-hint">60 to 1440 minutes.</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="eu-cron-jobs">Max cron jobs</label>
                <input name="cron_max_jobs" id="eu-cron-jobs" type="number" className="form-input" value={formData.cron_max_jobs ?? ''} onChange={handleChange} min="1" max="20" />
                <div className="form-hint">1 to 20.</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="eu-subagents">Max sub-agents</label>
                <input name="max_concurrent_subagents" id="eu-subagents" type="number" className="form-input" value={formData.max_concurrent_subagents ?? ''} onChange={handleChange} min="1" max="10" />
                <div className="form-hint">Running at the same time, 1 to 10.</div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="eu-timezone">Timezone</label>
                <select name="timezone" id="eu-timezone" className="form-input" value={formData.timezone} onChange={handleChange}>
                  {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="eu-exec-mode">Exec mode</label>
                <select name="exec_mode" id="eu-exec-mode" className="form-input" value={formData.exec_mode} onChange={handleChange}>
                  {EXEC_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <div className="form-hint">Controls which shell commands the agent can execute.</div>
              </div>
            </div>
          </section>

          <section className="card danger-zone" aria-labelledby="eu-maintenance">
            <SectionHead id="eu-maintenance" title="Maintenance" />
            <div className="setting-row p-0">
              <div className="min-w-0">
                <label className="setting-row-title" htmlFor="eu-clear-sessions">Clear chat history</label>
                <p className="setting-row-desc">Deletes every saved conversation session when the agent redeploys.</p>
              </div>
              <Switch id="eu-clear-sessions" checked={formData.clear_sessions} onChange={v => set('clear_sessions', v)} />
            </div>
            {formData.clear_sessions && (
              <div className="notice notice-danger mt-3 mb-0" role="status">
                <Icon name="alert" />
                <div className="notice-body">All saved conversation sessions will be deleted on save.</div>
              </div>
            )}
          </section>

          <SaveBar>
            {!canRedeploy && <span className="text-sm text-muted grow">{drainingNote}</span>}
            {cancelButton}
            <button type="submit" className="btn btn-primary" disabled={!canRedeploy || saving}>
              {saveLabel(<><Icon name="refresh" /> Save and redeploy</>)}
            </button>
          </SaveBar>
        </form>
      )}

      {activeTab === 'tools' && (
        <form onSubmit={handleSaveTools} aria-label="Tools">
          {/* Three cards for the three places a tool lives (Mark, 2026-11-02): the runtime,
              proxy-tools, and the workspace. Each reaches the agent at a different moment,
              which is what each subtitle says. */}
          <section className="card" aria-labelledby="eu-tools-internal">
            <SectionHead
              id="eu-tools-internal"
              title="Internal tools"
              subtitle="Changes apply the next time the agent starts."
            />
            <SkillsTable
              categories={['internal']}
              enabledSkills={enabledSkills}
              onToggleSkill={toggleSkill}
              onSetAllSkills={setEnabledSkills}
              skipDefaults={true}
            />
          </section>

          <section className="card" aria-labelledby="eu-tools-platform">
            <SectionHead
              id="eu-tools-platform"
              title="Platform tools"
              subtitle="Served by proxy-tools. Changes apply from the agent's next turn, without a restart; the built-in speech and image tools at its next start."
            />
            <SkillsTable
              categories={['external', 'rossgate', 'mcp']}
              enabledSkills={enabledSkills}
              onToggleSkill={toggleSkill}
              onSetAllSkills={setEnabledSkills}
              skipDefaults={true}
              userId={userId}
              userManaged={userManaged}
              mandatoryTools={mandatoryTools}
              onToggleUserManaged={(id, on) => setUserManaged(prev => (prev ? { ...prev, [id]: on } : prev))}
            />
          </section>

          <section className="card" aria-labelledby="eu-tools-apps">
            <SectionHead
              id="eu-tools-apps"
              title="Apps and guides"
              subtitle="Changes apply the next time the agent starts."
            />
            <SkillsTable
              categories={['apps']}
              enabledApps={enabledApps}
              onToggleApp={(id, on) => setEnabledApps(prev => on ? [...prev, id] : prev.filter(s => s !== id))}
              onSetAllApps={setEnabledApps}
              skipDefaults={true}
            />
          </section>

          <SaveBar>
            {cancelButton}
            <button type="submit" className="btn btn-primary" disabled={saving}>{saveLabel('Save tools')}</button>
          </SaveBar>
        </form>
      )}

      {activeTab === 'account' && (
        <form onSubmit={handleSaveAccount} aria-label="Account">
          <section className="card" aria-labelledby="eu-identity">
            <SectionHead id="eu-identity" title="Account" subtitle="Identity and contact details. Saving this tab does not redeploy the agent." />
            <dl className="kv mb-5">
              <dt>Username</dt>
              <dd className="mono">{user.username}</dd>
              <dt>
                External ID
                <InfoTip label="External ID">
                  <code>interface_external_user_id</code>: set by the website, otherwise assigned automatically.
                </InfoTip>
              </dt>
              <dd className="mono">{user.interface_external_user_id || '—'}</dd>
              <dt>
                Internal chat ID
                <InfoTip label="Internal chat ID">
                  One id for every channel. Allocated at creation from the user&apos;s channels key,
                  above Telegram&apos;s id range so it can never collide with a real chat.
                  proxy-channels swaps it for the real address on the way out, so the agent never
                  sees a real Telegram id. Empty means the user predates the column and keeps legacy
                  per-channel ids until the next backfill.
                </InfoTip>
              </dt>
              <dd className="mono">
                {user.internal_chat_id || <span className="text-faint">Not allocated (legacy per-channel ids)</span>}
              </dd>
            </dl>
            <div className="form-group mb-0" style={{ maxWidth: 480 }}>
              <label className="form-label" htmlFor="eu-email">Email</label>
              <input name="email" id="eu-email" type="email" className="form-input" value={formData.email} onChange={handleChange} placeholder="Optional" autoComplete="off" />
              <div className="form-hint">Leave it empty to remove the address.</div>
            </div>
          </section>

          {/* MCP server keys: one row per key the user has (the RossGate sk-gw-… today),
              masked. Minted by the control-plane when the user boots holding the server,
              never typed in: the paste field and the Provision button are gone. Absent for
              a user with no key, which is also every user on an environment still running
              Maton. The agent never sees a key; proxy-tools attaches it server-side. */}
          {credentials.length > 0 && (
            <section className="card" aria-labelledby="eu-mcp-keys">
              <SectionHead id="eu-mcp-keys" title="MCP server keys"
                           subtitle="Minted automatically. The agent never sees them." />
              <div className="stack gap-2">
                {credentials.map(c => (
                  <div key={c.server} className="row-wrap gap-2" style={{ alignItems: 'center' }}>
                    <code style={{ minWidth: 90 }}>{c.server}</code>
                    {c.status === 'active' && (
                      <>
                        <span className="copy-field" style={{ minWidth: 160 }}>{c.key_hint || 'key'}</span>
                        {c.in_use
                          ? <Badge tone="ok" dot>Active</Badge>
                          : <Badge tone="neutral">Not in use</Badge>}
                      </>
                    )}
                    {(c.status === 'pending' || c.status === 'minting') && <Badge tone="info" dot>Minting</Badge>}
                    {c.status === 'invalid' && <Badge tone="warn" dot>Refused, being re-minted</Badge>}
                    {c.status === 'failed' && <Badge tone="danger" dot>Failed</Badge>}
                    {c.status === 'revoked' && <Badge tone="neutral">Revoked</Badge>}
                    {c.status === 'revoke_failed' && <Badge tone="danger" dot>Revoke failed, retrying</Badge>}
                    <span className="grow" />
                    {c.status === 'active' && (
                      <>
                        <button type="button" className="btn btn-secondary btn-sm"
                                disabled={credBusy === c.server || !c.has_key_id}
                                title={c.has_key_id ? 'Mint a new key; the old one stops working after a short grace.'
                                  : 'This key predates key ids, so it cannot be rotated yet.'}
                                onClick={() => handleCredentialAction(c.server, 'rotate')}>
                          <Icon name="refresh" /> Rotate
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm"
                                disabled={credBusy === c.server || !c.has_key_id}
                                title={c.has_key_id ? 'Revoke this key at the server.'
                                  : 'This key predates key ids, so it can only be revoked by deleting the user.'}
                                onClick={() => handleCredentialAction(c.server, 'revoke')}>
                          <Icon name="x" /> Revoke
                        </button>
                      </>
                    )}
                    {['failed', 'invalid', 'pending', 'revoked'].includes(c.status) && (
                      <button type="button" className="btn btn-secondary btn-sm"
                              disabled={credBusy === c.server}
                              onClick={() => handleCredentialAction(c.server, 'retry')}>
                        {credBusy === c.server ? <><span className="spinner-sm" /> Queueing…</> : <><Icon name="key" /> Retry now</>}
                      </button>
                    )}
                    <div className="form-hint" style={{ flexBasis: '100%' }}>
                      {c.status === 'active' && c.minted_at && <>Since {new Date(c.minted_at).toLocaleDateString()}</>}
                      {c.account_id && <> · account <code>{c.account_id}</code></>}
                      {c.status === 'failed' && c.last_error && <>{c.last_error}</>}
                      {c.status === 'failed' && c.next_attempt_at && <> · next try {new Date(c.next_attempt_at).toLocaleString()}</>}
                      {c.status === 'failed' && !c.next_attempt_at && <> · not retried automatically</>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="card" aria-labelledby="eu-channels">
            <SectionHead
              id="eu-channels"
              title="Channels"
              subtitle="Switching a channel off, or clearing its address, unpairs it; the other channels are unaffected."
            />
            <ChannelRow
              id="eu-ch-telegram"
              title="Telegram"
              description="The person's Telegram account and the bot credential that serves it."
              checked={telegramEnabled}
              onChange={on => { setTelegramEnabled(on); clearErrors('telegram_id'); }}
            >
              {telegramEnabled && (
                <div className="card-inset mt-3">
                  <div className="form-group">
                    <label className="form-label" htmlFor="eu-telegram-id">
                      Telegram user ID<span className="required" aria-hidden="true">*</span>
                    </label>
                    <input name="telegram_id" id="eu-telegram-id" type="text" className="form-input" value={formData.telegram_id} onChange={handleChange} placeholder="123456789" inputMode="numeric" autoComplete="off" aria-invalid={errors.telegram_id ? true : undefined} aria-describedby="eu-telegram-id-hint" />
                    {fieldError('telegram_id')}
                    <div className="form-hint" id="eu-telegram-id-hint">Get it from @userinfobot on Telegram.</div>
                  </div>
                  <div className="form-group mb-0" role="radiogroup" aria-labelledby="eu-bot-label">
                    <div className="form-label" id="eu-bot-label">Bot</div>
                    <div className="row-wrap gap-4">
                      <label className="check-row">
                        <input type="radio" name="telegram_bot_choice" checked={!useCustomBot} onChange={() => setUseCustomBot(false)} />
                        Shared (default)
                      </label>
                      <label className="check-row">
                        <input type="radio" name="telegram_bot_choice" checked={useCustomBot} onChange={() => setUseCustomBot(true)} />
                        Custom bot
                      </label>
                    </div>
                    {!useCustomBot && (
                      <div className="form-hint">
                        Messages are routed through the platform&apos;s shared bot. Which pool bot
                        serves this user is set on
                        the <button type="button" className="btn btn-link text-sm" onClick={() => setActiveTab('channels')}>Telegram bot tab</button>.
                      </div>
                    )}
                    {useCustomBot && (
                      <div className="mt-3">
                        {!editingBotToken ? (
                          <div className="copy-field">
                            <span className={formData.telegram_bot_token ? undefined : 'text-danger'}>
                              {formData.telegram_bot_token || 'Not configured'}
                            </span>
                            <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => setEditingBotToken(true)}
                                    aria-label="Replace bot token" title="Replace bot token">
                              <Icon name="edit" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <label className="form-label" htmlFor="eu-bot-token">New bot token</label>
                            <div className="input-group">
                              <input id="eu-bot-token" type="text" className="form-input mono" value={formData.newBotToken} onChange={e => set('newBotToken', e.target.value)} placeholder="123456:ABC-DEF…" autoComplete="off" spellCheck={false} />
                              <button type="button" className="btn btn-ghost" onClick={() => { setEditingBotToken(false); set('newBotToken', ''); }}>Cancel</button>
                            </div>
                            <div className="form-hint">Get it from @BotFather on Telegram. Replaces the current token when you save.</div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ChannelRow>

            {/* RossHub, the operator-facing name for the `website` channel. Toggled the
              * same way as Telegram: off means the channel is not enabled and the member
              * ID is submitted empty, which unpairs it. There is no bot here — one API,
              * one credential — so the address is the whole configuration. Editable so an
              * operator who knows somebody's member ID can pair them without waiting for
              * the person to sign in and Sync. */}
            <ChannelRow
              id="eu-ch-website"
              title="RossHub"
              description="Pair a RossHub member directly, without waiting for them to sign in and Sync."
              checked={websiteEnabled}
              onChange={on => { setWebsiteEnabled(on); clearErrors('website_id'); }}
            >
              {websiteEnabled && (
                <div className="card-inset mt-3">
                  <div className="form-group mb-0">
                    <label className="form-label" htmlFor="eu-website-id">
                      RossHub member ID<span className="required" aria-hidden="true">*</span>
                    </label>
                    <input
                      name="website_id"
                      id="eu-website-id"
                      type="text"
                      className="form-input"
                      value={formData.website_id}
                      onChange={handleChange}
                      placeholder="4820193746"
                      inputMode="numeric"
                      autoComplete="off"
                      aria-invalid={errors.website_id ? true : undefined}
                      aria-describedby="eu-website-id-hint"
                    />
                    {fieldError('website_id')}
                    <div className="form-hint" id="eu-website-id-hint">The 10-digit member ID from RossHub.</div>
                  </div>
                </div>
              )}
            </ChannelRow>

            {/* Microsoft Teams. One Azure Bot serves the whole tenant, so this address is
              * the only thing that identifies a sender — there is no per-user credential
              * to fall back on. It must be the Entra object id, which is what arrives on
              * the activity; an email cannot be matched without Graph. */}
            <ChannelRow
              id="eu-ch-teams"
              title="Microsoft Teams"
              description="One bot serves the whole tenant, so the Object ID is the only thing that identifies this person."
              checked={teamsEnabled}
              onChange={on => { setTeamsEnabled(on); clearErrors('teams_id'); }}
            >
              {teamsEnabled && (
                <div className="card-inset mt-3">
                  <div className="form-group mb-0">
                    <label className="form-label" htmlFor="eu-teams-id">
                      Entra Object ID<span className="required" aria-hidden="true">*</span>
                    </label>
                    <input
                      name="teams_id"
                      id="eu-teams-id"
                      type="text"
                      className="form-input mono"
                      value={formData.teams_id}
                      onChange={handleChange}
                      placeholder="3f2504e0-4f89-11d3-9a0c-0305e82c3301"
                      autoComplete="off"
                      spellCheck={false}
                      aria-invalid={errors.teams_id ? true : undefined}
                      aria-describedby="eu-teams-id-hint"
                    />
                    {fieldError('teams_id')}
                    <div className="form-hint" id="eu-teams-id-hint">
                      Microsoft Entra admin center: Users &rarr; the person &rarr; <b>Object ID</b>. Not their email.
                    </div>
                  </div>
                </div>
              )}
            </ChannelRow>

            {/* The phone (`voice`). The number the user calls is their address on the
              * channel, and exactly one number may ring it: the caller Twilio reports must
              * match, or the call is refused before it is answered. What the call runs on
              * is the tier's (Tiers, Phone calls). Only where the environment takes calls. */}
            {phoneOn && (
              <ChannelRow
                id="eu-ch-voice"
                title="Phone"
                description="Talk to the agent on a phone call. Only the allowed number can call, and the tier decides the voice and the models."
                checked={voiceEnabled}
                onChange={on => { setVoiceEnabled(on); clearErrors('voice_number'); clearErrors('voice_caller'); }}
              >
                {voiceEnabled && (
                  <div className="card-inset mt-3">
                    <div className="grid-2">
                      <div className="form-group mb-0">
                        <label className="form-label" htmlFor="eu-voice-number">
                          Number to call<span className="required" aria-hidden="true">*</span>
                        </label>
                        <input
                          name="voice_number"
                          id="eu-voice-number"
                          type="tel"
                          className="form-input mono"
                          value={formData.voice_number}
                          onChange={handleChange}
                          placeholder="+39 02 0000 0000"
                          autoComplete="off"
                          aria-invalid={errors.voice_number ? true : undefined}
                          aria-describedby="eu-voice-number-hint"
                        />
                        {fieldError('voice_number')}
                        <div className="form-hint" id="eu-voice-number-hint">The platform&apos;s number this user dials, in international form.</div>
                      </div>
                      <div className="form-group mb-0">
                        <label className="form-label" htmlFor="eu-voice-caller">
                          Allowed caller<span className="required" aria-hidden="true">*</span>
                        </label>
                        <input
                          name="voice_caller"
                          id="eu-voice-caller"
                          type="tel"
                          className="form-input mono"
                          value={formData.voice_caller}
                          onChange={handleChange}
                          placeholder="+39 333 000 0000"
                          autoComplete="off"
                          aria-invalid={errors.voice_caller ? true : undefined}
                          aria-describedby="eu-voice-caller-hint"
                        />
                        {fieldError('voice_caller')}
                        <div className="form-hint" id="eu-voice-caller-hint">The user&apos;s own phone: calls from any other number are refused.</div>
                      </div>
                    </div>
                  </div>
                )}
              </ChannelRow>
            )}
          </section>

          {/* Tier Section — inlined from the now-removed TierModal in Users.jsx.
              The DB swap is live (services/users.py:480-483); proxy-llm resolves
              the new tier on every request, so no redeploy is required. */}
          <section className="card" aria-labelledby="eu-tier">
            <SectionHead
              id="eu-tier"
              title="Tier"
              subtitle="Takes effect on the next request; in-flight conversations are not interrupted."
            />
            <div className="form-group mb-0" style={{ maxWidth: 480 }}>
              <label className="form-label" htmlFor="tier-select">Tier</label>
              <select id="tier-select" name="tier_id" className="form-input" value={formData.tier_id} onChange={handleChange}>
                <option value="">No tier (unlimited, no cost cap)</option>
                {(tiers || []).map(tier => (
                  <option key={tier.id} value={tier.id}>
                    {formatTierLabel(tier)}
                  </option>
                ))}
              </select>
              <div className="form-hint row-wrap gap-2">
                <span>Currently <strong className="text-strong">{savedTierName || 'No tier'}</strong></span>
                {tierChanged && <Badge tone="warn">Changes on save</Badge>}
              </div>
            </div>
          </section>

          <SaveBar>
            {cancelButton}
            <button type="submit" className="btn btn-primary" disabled={saving}>{saveLabel('Save account')}</button>
          </SaveBar>
        </form>
      )}

      {activeTab === 'status' && (
        <form onSubmit={handleSaveStatus} aria-label="Status">
          {/* Status Section — source-agnostic: edits is_active +
              expiration regardless of whether the user came in via
              coupon, Stripe or manual creation. Active flag gates
              the proxy-channels message pipeline; expiration date
              feeds the coupon_lifecycle expiry sweep. */}
          <section className="card" aria-labelledby="eu-status">
            <SectionHead
              id="eu-status"
              title="Status"
              subtitle="Whether the user can reach the agent, and when that ends. Saving this tab does not redeploy."
            />
            <div className="setting-row p-0">
              <div className="min-w-0">
                <label className="setting-row-title" htmlFor="eu-active">Active</label>
                <p className="setting-row-desc">
                  When off, the user&apos;s messages are refused at the channel layer and
                  the <code>/riavvia</code> command cannot restart the agent. Switching it back
                  on clears any stale &quot;coupon stopped&quot; marker, so the expiry sweep can
                  fire normally on the next expiration.
                </p>
              </div>
              <Switch id="eu-active" checked={formData.is_active} onChange={v => set('is_active', v)} />
            </div>
            <div className="divider" />
            <div className="form-group mb-0" style={{ maxWidth: 420 }}>
              <label className="form-label" htmlFor="eu-expires">Expires on</label>
              <div className="input-group">
                <input
                  name="expires_at"
                  id="eu-expires"
                  type="datetime-local"
                  className="form-input"
                  value={formData.expires_at}
                  onChange={handleChange}
                  aria-describedby="eu-expires-hint"
                />
                {formData.expires_at && (
                  <button type="button" className="btn btn-ghost" onClick={() => set('expires_at', '')}>Clear</button>
                )}
              </div>
              <div className="form-hint" id="eu-expires-hint">
                Applies whatever the user&apos;s source. Stored on the lifecycle column the expiry
                sweep watches; leave it empty to clear any pending expiration.
              </div>
            </div>
          </section>

          <SaveBar>
            {cancelButton}
            <button type="submit" className="btn btn-primary" disabled={saving}>{saveLabel('Save status')}</button>
          </SaveBar>
        </form>
      )}

      {activeTab === 'channels' && (
        <form onSubmit={handleSaveChannels} aria-label="Telegram bot">
          <section className="card" aria-labelledby="eu-bot">
            <SectionHead
              id="eu-bot"
              title="Telegram bot"
              subtitle="The pool bot this user is assigned to."
            />
            <p className="section-lede">
              A change takes effect within about 15 seconds, without re-provisioning the agent.
              Left empty, the customer is served by their custom bot token if they have one,
              otherwise by the platform&apos;s default bot. Nothing assigns a pool bot again
              automatically.
            </p>
            <div className="form-group mb-0" style={{ maxWidth: 480 }}>
              <label className="form-label" htmlFor="telegram-bot-select">Assigned bot</label>
              <select
                id="telegram-bot-select"
                name="telegram_bot_id"
                className="form-input"
                value={formData.telegram_bot_id}
                onChange={handleChange}
              >
                <option value="">No pool bot</option>
                {(bots || []).map(bot => (
                  <option key={bot.id} value={bot.id}>
                    @{bot.username}
                    {typeof bot.user_count === 'number' ? `, ${bot.user_count} users` : ''}
                  </option>
                ))}
              </select>
              <div className="form-hint row-wrap gap-2">
                <span>Currently <strong className="text-strong">{savedBotName ? `@${savedBotName}` : 'No pool bot'}</strong></span>
                {botChanged && <Badge tone="warn">Changes on save</Badge>}
              </div>
            </div>
          </section>

          <SaveBar>
            {cancelButton}
            <button type="submit" className="btn btn-primary" disabled={saving}>{saveLabel('Save bot')}</button>
          </SaveBar>
        </form>
      )}

      {confirmDialog}
    </div>
  );
}

export default EditUser;
